import React, { useState, useRef, useEffect } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useLeaflet } from 'react-leaflet';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import lineChunk from '@turf/line-chunk';
import { lineString } from '@turf/helpers';

import { isMobile } from '~/core/scripts/utils';
import config from '~/scripts/config';
import * as S from '../MapDrawer.styles';

function getAnimation(coords) {
  const steps = coords.map((c, i) => {
    return `${(i / (coords.length - 1)) * 100}% { transform: translate(${
      c.x
    }px, ${c.y}px) rotate(${c.deg}deg); opacity: ${c.opacity} }`;
  });

  return keyframes`
    ${steps.join('\n')}
  `;
}

const Scissor = styled(S.Scissor)`
  animation: ${(p) => getAnimation(p.coords)} 5000ms 0ms linear;
  animation-fill-mode: forwards;
`;

const IntroAnimation = () => {
  const [currentAni, setCurrentAni] = useState(0);
  const scissorRef = useRef();
  const { map } = useLeaflet();
  const isDrawing = useStoreState((state) => state.app.isDrawing);
  const drawingErrorMessage = useStoreState(
    (state) => state.app.drawingErrorMessage
  );
  const showDrawEnableOverlay = useStoreState(
    (state) => state.app.showDrawEnableOverlay
  );
  const setDrawEnableOverlay = useStoreActions(
    (state) => state.app.setDrawEnableOverlay
  );
  const line = lineString(config.animationCoords[currentAni]);
  const chunks = lineChunk(line, 20);
  const points = chunks.features.map((feat) => {
    return feat.geometry.coordinates[0];
  });

  const coords = points
    .map((coord) => map.latLngToContainerPoint([coord[1], coord[0]]))
    .map((coord, i, all) => {
      const next = all[i + 1] || all[all.length - 1];
      const dx = coord.x - next.x;
      const dy = coord.y - next.y;
      const theta = Math.atan2(dy, dx);

      return {
        x: coord.x,
        y: coord.y,
        deg: theta * (180 / Math.PI) - 180,
        opacity: i === 0 || i >= all.length - 2 ? 0 : 1,
      };
    });

  useEffect(() => {
    const onAnimationEnd = () => {
      setCurrentAni((curr) =>
        curr + 1 > config.animationCoords.length - 1 ? 0 : curr + 1
      );
    };

    scissorRef.current.addEventListener('animationend', onAnimationEnd);

    return () => {
      scissorRef.current.removeEventListener('animationend', onAnimationEnd);
    };
  }, []);

  const CallToAction =
    isMobile || showDrawEnableOverlay ? null : (
      <S.BlackCircle isDrawing={isDrawing}>
        <span>Zerschneide die Karte mit der Maus in zwei Teile</span>
      </S.BlackCircle>
    );

  const handleOverlayClick = () => {
    setDrawEnableOverlay(false);
  };

  return (
    <S.ScissorContainer
      isOverlay={showDrawEnableOverlay}
      onClick={handleOverlayClick}
    >
      <Scissor
        ref={scissorRef}
        coords={coords}
        isDrawing={isDrawing}
        showDrawEnableOverlay={showDrawEnableOverlay}
      />
      {drawingErrorMessage ? (
        <S.ErrorMessage>
          <span>{drawingErrorMessage}</span>
        </S.ErrorMessage>
      ) : (
        CallToAction
      )}
    </S.ScissorContainer>
  );
};

export default IntroAnimation;
